import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import {
  addEmployeeBenefit,
  addEmployeeCertificate,
  addEmployeeContract,
  addEmployeeDisciplinaryMeasure,
  addEmployeePermit,
  addEmployeeWorkBooklet,
  addFamilyMember,
  deleteEmployeeBenefit,
  deleteEmployeeCertificate,
  deleteEmployeeContract,
  deleteEmployeeDisciplinaryMeasure,
  deleteEmployeePermit,
  deleteEmployeeWorkBooklet,
  deleteFamilyMember,
  getAnnualLeaveDays,
  getEmployeeCertificates,
  getEmployeeDocuments,
  getEmployeeFamilyMembers,
  getEmployeePermits,
  getEmployeeWorkBooklet,
  updateEmployeeAnnualLeaveDays,
  updateEmployeeBenefit,
  updateEmployeeCertificate,
  updateEmployeeContract,
  updateEmployeeDisciplinaryMeasure,
  updateEmployeePermit,
  updateEmployeeWorkBooklet,
  updateFamilyMember,
} from "../../Api/services/employeesService";
import { t } from "../../utils/common/i18nHelper";
import { updateWorkShift } from "../../Api/services/org-StructureService";

const employeesSlice = createSlice({
  name: "employees",
  initialState: {
    userCertificates: [],
    userWorkBooklets: [],
    userDocuments: [],
    familyMembers: [],
    userPermits: [],
    employeeAnnualLeaveDays: [],
    profilePhoto: null,
  },
  reducers: {
    setProfilePhoto: (state, action) => {
      state.profilePhoto = action.payload; // Update profile photo
    },
  },
  extraReducers: (builder) => {
    // Add Disciplinary Measure Reducer
    builder
      .addCase(addEmployeeDisciplinaryMeasure.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeeDisciplinaryMeasure.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.disciplinary_measure_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeeDisciplinaryMeasure.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Disciplinary Measure Reducer
    builder
      .addCase(updateEmployeeDisciplinaryMeasure.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeDisciplinaryMeasure.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.disciplinary_measure_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeDisciplinaryMeasure.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Disciplinary Measure Reducer
    builder
      .addCase(deleteEmployeeDisciplinaryMeasure.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeeDisciplinaryMeasure.fulfilled, (state, action) => {
        toast.success(t("employees.disciplinary_measure_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeeDisciplinaryMeasure.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Add Benefit Reducer
    builder
      .addCase(addEmployeeBenefit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeeBenefit.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.benefit_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeeBenefit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Benefit Reducer
    builder
      .addCase(updateEmployeeBenefit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeBenefit.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.benefit_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeBenefit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Benefit Reducer
    builder
      .addCase(deleteEmployeeBenefit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeeBenefit.fulfilled, (state, action) => {
        toast.success(t("employees.benefit_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeeBenefit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      }); // Add Contract Reducer
    builder
      .addCase(addEmployeeContract.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeeContract.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.contract_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeeContract.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Contract Reducer
    builder
      .addCase(updateEmployeeContract.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeContract.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.contract_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeContract.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Contract Reducer
    builder
      .addCase(deleteEmployeeContract.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeeContract.fulfilled, (state, action) => {
        toast.success(t("employees.contract_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeeContract.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Add Certificate Reducer
    builder
      .addCase(addEmployeeCertificate.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeeCertificate.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.certificate_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeeCertificate.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Certificate Reducer
    builder
      .addCase(updateEmployeeCertificate.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeCertificate.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.certificate_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeCertificate.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Certificate Reducer
    builder
      .addCase(deleteEmployeeCertificate.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeeCertificate.fulfilled, (state, action) => {
        toast.success(t("employees.certificate_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeeCertificate.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get all Certificates Reducer
    builder
      .addCase(getEmployeeCertificates.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getEmployeeCertificates.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          userCertificates: action?.payload?.data,
        };
      })
      .addCase(getEmployeeCertificates.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get all work booklets Reducer
    builder
      .addCase(getEmployeeWorkBooklet.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getEmployeeWorkBooklet.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          userWorkBooklets: action?.payload?.data,
        };
      })
      .addCase(getEmployeeWorkBooklet.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Add Work Booklet Reducer
    builder
      .addCase(addEmployeeWorkBooklet.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeeWorkBooklet.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.work_booklet_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeeWorkBooklet.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Work Booklet Reducer
    builder
      .addCase(updateEmployeeWorkBooklet.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeWorkBooklet.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.work_booklet_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeWorkBooklet.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Work Booklet Reducer
    builder
      .addCase(deleteEmployeeWorkBooklet.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeeWorkBooklet.fulfilled, (state, action) => {
        toast.success(t("employees.work_booklet_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeeWorkBooklet.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get all documents Reducer
    builder
      .addCase(getEmployeeDocuments.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getEmployeeDocuments.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          userDocuments: action?.payload?.data,
        };
      })
      .addCase(getEmployeeDocuments.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get all Family Members Reducer
    builder
      .addCase(getEmployeeFamilyMembers.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getEmployeeFamilyMembers.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          familyMembers: action?.payload?.data,
        };
      })
      .addCase(getEmployeeFamilyMembers.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Add Family Member Reducer
    builder
      .addCase(addFamilyMember.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addFamilyMember.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.family_member_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addFamilyMember.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Family Member Reducer
    builder
      .addCase(updateFamilyMember.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateFamilyMember.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.family_member_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateFamilyMember.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Family Member Reducer
    builder
      .addCase(deleteFamilyMember.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteFamilyMember.fulfilled, (state, action) => {
        toast.success(t("employees.family_member_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteFamilyMember.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get all Permits Reducer
    builder
      .addCase(getEmployeePermits.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getEmployeePermits.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          userPermits: action?.payload?.data,
        };
      })
      .addCase(getEmployeePermits.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Add Permit Reducer
    builder
      .addCase(addEmployeePermit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(addEmployeePermit.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.permit_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(addEmployeePermit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Update Permit Reducer
    builder
      .addCase(updateEmployeePermit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeePermit.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action.payload);
        } else {
          toast.success(t("employees.permit_update_success"));
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeePermit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });

    // Delete Permit Reducer
    builder
      .addCase(deleteEmployeePermit.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(deleteEmployeePermit.fulfilled, (state, action) => {
        toast.success(t("employees.permit_delete_success"));
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(deleteEmployeePermit.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Get employee annual leave days Reducer
    builder
      .addCase(getAnnualLeaveDays.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getAnnualLeaveDays.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          employeeAnnualLeaveDays: action?.payload?.data,
        };
      })
      .addCase(getAnnualLeaveDays.rejected, (state, action) => {
        toast.error(action.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
    // Update employee days of annual leave
    builder
      .addCase(updateEmployeeAnnualLeaveDays.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(updateEmployeeAnnualLeaveDays.fulfilled, (state, action) => {
        if (action?.payload?.status === false) {
          toast.error(action?.payload);
        } else {
          toast.success(action?.payload?.message);
        }
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(updateEmployeeAnnualLeaveDays.rejected, (state, action) => {
        toast.error(action?.payload);
        return {
          ...state,
          loading: false,
          error: action.payload,
        };
      });
  },
});

export const { setProfilePhoto } = employeesSlice.actions;
export default employeesSlice.reducer;
